import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Input,
  Select,
  SelectItem,
  CircularProgress,
  Textarea,
  Switch,
  Chip,
  Tooltip,
} from "@nextui-org/react";
import { BsTrash } from "react-icons/bs";
import { IoChevronDown, IoInformationCircleOutline } from "react-icons/io5";
import { LuSearch, LuPlus, LuListChecks } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import FilterDropdown from "../../components/FilterDropdown";
import myAxios from "../../utils/axiosWrapper";

// Mirrored in backend/api/department_config_generator.py (AI-generated fields
// are saved with the same suffixes): keep both in sync.
const FIELD_TYPE_SUFFIXES = {
  bool: ", put 1, otherwise put 0. You MUST put either 0 or 1.",
  number:
    ". Grade this with an integer between 0 and 10. You MUST respond with only a single integer from 0 to 10, nothing else.",
  freetext: ". Answer in a simple and detailed sentence.",
  category: (categories) =>
    `. You MUST respond with EXACTLY ONE of these categories: ${categories.join(
      ", "
    )}. Respond with only the category name, nothing else.`,
};

const FIELD_TYPES = [
  { key: "bool", label: "Yes/No" },
  { key: "number", label: "Note (0-10)" },
  { key: "freetext", label: "Texte libre" },
  { key: "category", label: "Catégorie" },
];

const CALL_DIRECTIONS = [
  { key: "all", label: "Tous les appels" },
  { key: "incoming", label: "Appels entrants" },
  { key: "outgoing", label: "Appels sortants" },
];

const FIELD_TYPE_CHIP_COLORS = {
  bool: "success",
  number: "primary",
  freetext: "default",
  category: "secondary",
};

const FILTERABLE_TYPES = ["bool", "category", "number"];

const EMPTY_FIELD = {
  name: "",
  display_name: "",
  instructions: "",
  type: "bool",
  filterable: false,
  enabled: true,
  hide_when_false: false,
  hide_when_true: false,
  department_ids: [],
  call_purpose_ids: [],
  call_direction: "all",
  categories: [],
  trigger_field: "",
  parent_fields: [],
};

// Convert DB format (underscores) to display format (spaces) and back
const dbToDisplay = (name) => name.replace(/_/g, " ");
const displayToDb = (name) => name.replace(/\s+/g, "_");

// Remove the type suffix from instructions for display
const getDisplayInstructions = (field) => {
  let suffix;
  if (field.type === "category" && field.categories?.length > 0) {
    suffix = FIELD_TYPE_SUFFIXES.category(field.categories);
  } else {
    suffix = FIELD_TYPE_SUFFIXES[field.type] || "";
  }

  if (suffix && field.instructions.endsWith(suffix)) {
    return field.instructions.slice(0, -suffix.length);
  }
  return field.instructions;
};

const CategoriesEditor = ({ categories, onChange, isReadOnly, error, t }) => {
  const [categoryInput, setCategoryInput] = useState("");

  const handleAdd = () => {
    const trimmed = categoryInput.trim();
    if (!trimmed || (categories || []).includes(trimmed)) return;
    onChange([...(categories || []), trimmed]);
    setCategoryInput("");
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-700">{t("Catégories disponibles")}</div>
      <div className="flex flex-wrap gap-2 p-3 bg-white border border-gray-200 rounded-lg min-h-[48px]">
        {(categories || []).map((category) => (
          <Chip
            key={category}
            onClose={
              isReadOnly ? undefined : () => onChange(categories.filter((c) => c !== category))
            }
            variant="flat"
            color="secondary"
            size="sm"
          >
            {category}
          </Chip>
        ))}
        {(!categories || categories.length === 0) && (
          <span className="text-xs text-gray-400">{t("Aucune catégorie définie")}</span>
        )}
      </div>
      {!isReadOnly && (
        <div className="flex gap-2">
          <Input
            placeholder={t("Ajouter une catégorie...")}
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            size="sm"
            classNames={{ input: "text-sm" }}
            isInvalid={!!error}
            errorMessage={error}
          />
          <Button
            size="sm"
            color="secondary"
            onClick={handleAdd}
            isIconOnly
          >
            <LuPlus size={18} />
          </Button>
        </div>
      )}
    </div>
  );
};

// Shared form for both editing an existing field and creating a new one.
const FieldForm = ({
  field,
  onChange,
  isReadOnly,
  isNew = false,
  allFields,
  departmentOptions,
  getDepartmentNames,
  errors = {},
  t,
}) => {
  const availableTriggerFields = allFields.filter(
    (f) => f.enabled && f.type === "bool" && displayToDb(f.name) !== displayToDb(field.name)
  );
  const availableParentFields = allFields.filter(
    (f) => displayToDb(f.name) !== displayToDb(field.name)
  );

  const handleTypeChange = (newType) => {
    if (!newType) return;
    onChange({
      type: newType,
      filterable: FILTERABLE_TYPES.includes(newType) ? field.filterable : false,
      categories: newType === "category" ? field.categories || [] : [],
    });
  };

  const suffix =
    field.type === "category" && field.categories?.length > 0
      ? FIELD_TYPE_SUFFIXES.category(field.categories)
      : FIELD_TYPE_SUFFIXES[field.type];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label={t("Nom du champ")}
          placeholder={t("ex: langage professionnel")}
          value={field.name}
          onChange={(e) => onChange({ name: e.target.value })}
          isReadOnly={!isNew || isReadOnly}
          variant={isNew && !isReadOnly ? "bordered" : "flat"}
          isInvalid={!!errors.name}
          errorMessage={errors.name}
          description={!isNew ? t("Le nom technique ne peut pas être modifié") : undefined}
          classNames={{ input: "text-sm", label: "text-xs font-medium" }}
        />
        <Input
          label={t("Nom d'affichage (optionnel)")}
          placeholder={t("ex: Langage professionnel")}
          value={field.display_name || ""}
          onChange={(e) => onChange({ display_name: e.target.value })}
          isReadOnly={isReadOnly}
          variant={isReadOnly ? "flat" : "bordered"}
          classNames={{ input: "text-sm", label: "text-xs font-medium" }}
        />
      </div>
      <Textarea
        label={t("Description de la condition")}
        placeholder={t("Décrivez ce que l'agent doit faire...")}
        value={getDisplayInstructions(field)}
        onChange={(e) => onChange({ instructions: e.target.value })}
        isReadOnly={isReadOnly}
        variant={isReadOnly ? "flat" : "bordered"}
        minRows={2}
        maxRows={6}
        isInvalid={!!errors.instructions}
        errorMessage={errors.instructions}
        classNames={{ input: "text-sm", label: "text-xs font-medium" }}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label={t("Type de champ")}
          selectedKeys={[field.type]}
          onChange={(e) => handleTypeChange(e.target.value)}
          isDisabled={isReadOnly}
          variant={isReadOnly ? "flat" : "bordered"}
          classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
        >
          {FIELD_TYPES.map((type) => (
            <SelectItem
              key={type.key}
              value={type.key}
            >
              {t(type.label)}
            </SelectItem>
          ))}
        </Select>
        <Select
          label={t("Direction d'appel")}
          selectedKeys={[field.call_direction || "all"]}
          onChange={(e) => e.target.value && onChange({ call_direction: e.target.value })}
          isDisabled={isReadOnly}
          variant={isReadOnly ? "flat" : "bordered"}
          classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
        >
          {CALL_DIRECTIONS.map((direction) => (
            <SelectItem
              key={direction.key}
              value={direction.key}
            >
              {t(direction.label)}
            </SelectItem>
          ))}
        </Select>
      </div>

      {field.type === "category" && (
        <CategoriesEditor
          categories={field.categories}
          onChange={(categories) => onChange({ categories })}
          isReadOnly={isReadOnly}
          error={errors.categories}
          t={t}
        />
      )}

      <div>
        <Select
          label={t("Départements")}
          placeholder={t("Tous les départements")}
          selectionMode="multiple"
          selectedKeys={new Set(field.department_ids?.map(String) || [])}
          onSelectionChange={(keys) => {
            onChange({ department_ids: Array.from(keys).map((k) => k.toString()) });
          }}
          isDisabled={isReadOnly}
          variant={isReadOnly ? "flat" : "bordered"}
          classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
          renderValue={(items) => {
            if (items.length === 0) {
              return <span className="text-gray-400">{t("Tous les départements")}</span>;
            }
            return (
              <div className="flex flex-wrap gap-1">
                {items.map((item) => (
                  <span
                    key={item.key}
                    className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded"
                  >
                    {item.textValue}
                  </span>
                ))}
              </div>
            );
          }}
        >
          {departmentOptions.map((dept) => (
            <SelectItem
              key={dept.id}
              value={dept.id}
            >
              {dept.label}
            </SelectItem>
          ))}
        </Select>
        {field.department_ids?.length === 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {t("Aucun département sélectionné = tous les départements")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Select
            label={t("Condition")}
            placeholder={t("Aucune condition")}
            selectedKeys={field.trigger_field ? [field.trigger_field] : []}
            onChange={(e) => onChange({ trigger_field: e.target.value || "" })}
            isDisabled={isReadOnly}
            variant={isReadOnly ? "flat" : "bordered"}
            classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
          >
            {availableTriggerFields.map((f) => (
              <SelectItem
                key={displayToDb(f.name)}
                value={displayToDb(f.name)}
              >
                {f.name}
              </SelectItem>
            ))}
          </Select>
          {field.trigger_field && (
            <p className="text-xs text-gray-500 mt-1">
              {t("Ce champ ne sera évalué que si")}{" "}
              <span className="font-medium">{dbToDisplay(field.trigger_field)}</span>{" "}
              {t("est vrai")}
            </p>
          )}
        </div>
        <div>
          <Select
            label={t("Lié à d'autres champs")}
            placeholder={t("Aucun champ parent")}
            selectionMode="multiple"
            selectedKeys={new Set(field.parent_fields || [])}
            onSelectionChange={(keys) => {
              onChange({ parent_fields: Array.from(keys).map((k) => k.toString()) });
            }}
            isDisabled={isReadOnly}
            variant={isReadOnly ? "flat" : "bordered"}
            classNames={{ trigger: "text-sm", label: "text-xs font-medium" }}
          >
            {availableParentFields.map((f) => (
              <SelectItem
                key={displayToDb(f.name)}
                value={displayToDb(f.name)}
              >
                {f.display_name || f.name}
              </SelectItem>
            ))}
          </Select>
          {field.parent_fields?.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {t("Affiché avec")}{" "}
              <span className="font-medium">
                {field.parent_fields.map((p) => dbToDisplay(p)).join(", ")}
              </span>
            </p>
          )}
        </div>
      </div>

      {FILTERABLE_TYPES.includes(field.type) && (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
          <div className="flex flex-col">
            <span className="text-xs font-medium text-gray-700">
              {t("Filtrable dans l'interface")}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {t("Permet de filtrer les appels par ce champ")}
              </span>
              <Tooltip
                content={t(
                  "Lorsque activé, ce champ pourra être utilisé comme filtre dans le listing. L'activation d'un nouveau filtre peut prendre plusieures minutes."
                )}
                classNames={{ content: "max-w-xs text-sm" }}
              >
                <div className="cursor-pointer pb-0.5">
                  <IoInformationCircleOutline
                    size={16}
                    className="text-gray-400 mt-1"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
          <Switch
            isSelected={field.filterable}
            onValueChange={(value) => onChange({ filterable: value })}
            isDisabled={isReadOnly}
            size="sm"
          />
        </div>
      )}

      {field.type === "bool" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
            <div className="flex flex-col pr-2">
              <span className="text-xs font-medium text-gray-700">
                {t("Masquer si la valeur est faux")}
              </span>
              <span className="text-xs text-gray-500">
                {t("Le champ ne s'affichera dans l'analyse de l'appel que si la valeur est vraie")}
              </span>
            </div>
            <Switch
              isSelected={field.hide_when_false}
              onValueChange={(value) =>
                onChange({
                  hide_when_false: value,
                  hide_when_true: value ? false : field.hide_when_true,
                })
              }
              isDisabled={isReadOnly}
              size="sm"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-md">
            <div className="flex flex-col pr-2">
              <span className="text-xs font-medium text-gray-700">
                {t("Masquer si la valeur est vrai")}
              </span>
              <span className="text-xs text-gray-500">
                {t("Le champ ne s'affichera dans l'analyse de l'appel que si la valeur est fausse")}
              </span>
            </div>
            <Switch
              isSelected={field.hide_when_true}
              onValueChange={(value) =>
                onChange({
                  hide_when_true: value,
                  hide_when_false: value ? false : field.hide_when_false,
                })
              }
              isDisabled={isReadOnly}
              size="sm"
            />
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-md">
        <span className="font-medium">{t("Suffixe automatique")}: </span>
        <span className="italic">{suffix}</span>
      </div>
    </div>
  );
};

const CustomFieldsPanel = ({
  apiUrl,
  organisations,
  company_config,
  isReadOnly = false,
  onDirtyChange = () => {},
  refreshKey = 0,
}) => {
  const { t } = useTranslation();

  const [customFields, setCustomFields] = useState([]);
  const [allFieldsIncludingDeleted, setAllFieldsIncludingDeleted] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedNames, setExpandedNames] = useState(() => new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [newField, setNewField] = useState(EMPTY_FIELD);
  const [errors, setErrors] = useState({});
  const [listSearch, setListSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const initialSnapshotRef = useRef("[]");

  const isDirty = JSON.stringify(customFields) !== initialSnapshotRef.current;

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty]);

  const fetchCustomFields = async () => {
    setIsLoading(true);
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/admin/get_company_custom_analysis_fields`
      );
      const data = response?.data?.[0] || {};

      const allFieldsWithDisplayNames = (data?.custom_fields || []).map((field) => ({
        ...field,
        name: dbToDisplay(field.name),
        display_name: field.display_name || "",
        filterable: field.filterable ?? false,
        enabled: field.enabled ?? true,
        hide_when_false: field.hide_when_false ?? false,
        hide_when_true: field.hide_when_true ?? false,
        department_ids: field.department_ids || (field.department_id ? [field.department_id] : []),
        call_purpose_ids: field.call_purpose_ids || [],
        call_direction: field.call_direction || "all",
        categories: field.categories || [],
        trigger_field: field.trigger_field || "",
        parent_fields: field.parent_fields || (field.parent_field ? [field.parent_field] : []),
      }));

      setAllFieldsIncludingDeleted(allFieldsWithDisplayNames);

      const activeFields = allFieldsWithDisplayNames.filter((field) => !field.deleted);
      setCustomFields(activeFields);
      initialSnapshotRef.current = JSON.stringify(activeFields);
    } catch (error) {
      console.error("Error fetching custom fields:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomFields();
  }, [refreshKey]);

  const validateFieldName = (name) => {
    if (!name) return t("Name is required");
    if (!/^[a-zA-Z0-9 ]+$/.test(name)) {
      return t("Name can only contain alphanumeric characters and spaces");
    }

    const dbName = displayToDb(name);
    const isDuplicate =
      allFieldsIncludingDeleted.some((field) => displayToDb(field.name) === dbName) ||
      customFields.some((field) => displayToDb(field.name) === dbName);

    if (isDuplicate) {
      return t("Name must be unique (a field with this name already exists or existed)");
    }

    return null;
  };

  const validateField = (field) => {
    const fieldErrors = {};
    const nameError = validateFieldName(field.name);
    if (nameError) fieldErrors.name = nameError;
    if (!field.instructions?.trim()) fieldErrors.instructions = t("Instructions are required");
    if (!field.type) fieldErrors.type = t("Type is required");
    if (field.type === "category" && (!field.categories || field.categories.length === 0)) {
      fieldErrors.categories = t("At least one category is required");
    }
    return fieldErrors;
  };

  const handleAddField = () => {
    const fieldErrors = validateField(newField);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setCustomFields([...customFields, { ...newField }]);
    setNewField(EMPTY_FIELD);
    setErrors({});
    setIsCreating(false);
    setExpandedNames((previous) => new Set(previous).add(newField.name));
  };

  const handleUpdateField = (index, updates) => {
    const updatedFields = [...customFields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setCustomFields(updatedFields);
  };

  const handleDeleteField = (index) => {
    const deletedFieldName = displayToDb(customFields[index]?.name || "");
    // Backend handles soft deletion of removed fields; clean dangling references.
    const remaining = customFields
      .filter((_, idx) => idx !== index)
      .map((field) => {
        const next = { ...field };
        if (next.trigger_field === deletedFieldName) next.trigger_field = "";
        if (Array.isArray(next.parent_fields)) {
          next.parent_fields = next.parent_fields.filter((p) => p !== deletedFieldName);
        }
        return next;
      });
    setCustomFields(remaining);
  };

  const handleCancel = () => {
    setCustomFields(JSON.parse(initialSnapshotRef.current));
    setErrors({});
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const fieldsWithSuffixes = customFields.map((field) => {
        let suffix;
        if (field.type === "category" && field.categories?.length > 0) {
          suffix = FIELD_TYPE_SUFFIXES.category(field.categories);
        } else {
          suffix = FIELD_TYPE_SUFFIXES[field.type] || "";
        }

        return {
          ...field,
          name: displayToDb(field.name),
          instructions: getDisplayInstructions(field) + suffix,
          trigger_field: field.trigger_field ? displayToDb(field.trigger_field) : "",
          parent_fields: (field.parent_fields || []).map((p) => displayToDb(p)),
        };
      });

      await myAxios.put(`${apiUrl}/audio/api/v1/admin/update_company_custom_analysis_fields`, {
        custom_fields: fieldsWithSuffixes,
      });

      await fetchCustomFields();
    } catch (error) {
      console.error("Error saving custom fields:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getDepartmentNames = (departmentIds) => {
    if (!departmentIds || departmentIds.length === 0) return t("Tous les départements");
    const names = departmentIds
      .map((id) => {
        const department = organisations?.departments?.find((d) => d.id === id);
        return department?.name;
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : t("Départements inconnus");
  };

  const departmentOptions = (organisations?.departments || []).map((dept) => ({
    key: dept.id,
    label: dept.name,
    id: dept.id,
  }));

  const toggleExpanded = (name) => {
    setExpandedNames((previous) => {
      const next = new Set(previous);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const typeFilterOptions = [
    { key: "all", label: "All types" },
    ...FIELD_TYPES.map((type) => ({ key: type.key, label: type.label })),
  ];

  const statusFilterOptions = [
    { key: "all", label: "All statuses" },
    { key: "enabled", label: "Activé" },
    { key: "disabled", label: "Désactivé" },
  ];

  const visibleFields = customFields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => {
      if (typeFilter !== "all" && field.type !== typeFilter) return false;
      if (statusFilter === "enabled" && !field.enabled) return false;
      if (statusFilter === "disabled" && field.enabled) return false;
      if (listSearch.trim()) {
        const needle = listSearch.trim().toLowerCase();
        return (
          (field.name || "").toLowerCase().includes(needle) ||
          (field.display_name || "").toLowerCase().includes(needle)
        );
      }
      return true;
    });

  const enabledCount = customFields.filter((field) => field.enabled).length;

  const renderFieldSummaryChips = (field) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Chip
        size="sm"
        variant="flat"
        color={FIELD_TYPE_CHIP_COLORS[field.type] || "default"}
      >
        {t(FIELD_TYPES.find((type) => type.key === field.type)?.label || field.type)}
      </Chip>
      {field.department_ids?.length > 0 && (
        <Tooltip content={getDepartmentNames(field.department_ids)}>
          <Chip
            size="sm"
            variant="flat"
            color="warning"
          >
            {field.department_ids.length} {t("dept(s)")}
          </Chip>
        </Tooltip>
      )}
      {field.call_direction && field.call_direction !== "all" && (
        <Chip
          size="sm"
          variant="flat"
          color="secondary"
        >
          {t(CALL_DIRECTIONS.find((d) => d.key === field.call_direction)?.label || "")}
        </Chip>
      )}
      {field.trigger_field && (
        <Chip
          size="sm"
          variant="flat"
          className="bg-indigo-100 text-indigo-600"
        >
          {t("si")} {dbToDisplay(field.trigger_field)}
        </Chip>
      )}
      {field.filterable && (
        <Chip
          size="sm"
          variant="flat"
          className="bg-blue-100 text-blue-600"
        >
          {t("Filtrable")}
        </Chip>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <LuListChecks
              size={20}
              className="text-primary"
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#171717]">
              {t("Champs d'analyse personnalisés")}
            </h2>
            <p className="text-xs text-[#737373]">
              {customFields.length} {t("champs")} · {enabledCount} {t("activés")}
              {isReadOnly && ` · ${t("Lecture seule")}`}
            </p>
          </div>
        </div>
        {!isReadOnly && (
          <Button
            color="primary"
            startContent={<LuPlus size={16} />}
            onClick={() => setIsCreating(!isCreating)}
          >
            {t("Ajouter un champ")}
          </Button>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Input
          placeholder={t("Rechercher un champ...")}
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
          isClearable
          onClear={() => setListSearch("")}
          size="sm"
          className="w-[240px]"
          startContent={
            <LuSearch
              size={16}
              className="text-gray-400"
            />
          }
          classNames={{ input: "text-sm" }}
        />
        <FilterDropdown
          placeholder="All types"
          options={typeFilterOptions}
          value={typeFilter}
          onChange={setTypeFilter}
        />
        <FilterDropdown
          placeholder="All statuses"
          options={statusFilterOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {isCreating && !isReadOnly && (
        <div className="mt-4 border-2 border-dashed border-primary/30 bg-primary/[0.02] rounded-xl p-4">
          <h3 className="text-sm font-semibold text-[#171717] mb-3 flex items-center gap-2">
            <LuPlus size={16} />
            {t("Ajouter un nouveau champ")}
          </h3>
          <FieldForm
            field={newField}
            onChange={(updates) => {
              setNewField((previous) => ({ ...previous, ...updates }));
              const clearedErrors = { ...errors };
              Object.keys(updates).forEach((key) => {
                clearedErrors[key] = null;
              });
              setErrors(clearedErrors);
            }}
            isReadOnly={false}
            isNew
            allFields={customFields}
            departmentOptions={departmentOptions}
            getDepartmentNames={getDepartmentNames}
            errors={errors}
            t={t}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="flat"
              onClick={() => {
                setIsCreating(false);
                setNewField(EMPTY_FIELD);
                setErrors({});
              }}
            >
              {t("Annuler")}
            </Button>
            <Button
              color="primary"
              startContent={<LuPlus size={16} />}
              onClick={handleAddField}
            >
              {t("Ajouter le champ")}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress />
          </div>
        ) : (
          <div className="space-y-2">
            {customFields.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-12">
                {t("Aucun champ d'analyse personnalisé")}
              </div>
            )}
            {customFields.length > 0 && visibleFields.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-12">{t("Aucun résultat")}</div>
            )}
            {visibleFields.map(({ field, index }) => {
              const isExpanded = expandedNames.has(field.name);
              return (
                <div
                  key={field.name}
                  className={`border rounded-xl transition-colors ${
                    !field.enabled
                      ? "border-gray-200 bg-gray-50 opacity-70"
                      : isExpanded
                      ? "border-primary/40"
                      : "border-[#E5E5E5]"
                  }`}
                >
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer select-none"
                    onClick={() => toggleExpanded(field.name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") toggleExpanded(field.name);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <IoChevronDown
                      size={16}
                      className={`text-gray-400 shrink-0 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#171717] truncate">
                        {field.display_name || field.name}
                      </div>
                      {field.display_name && (
                        <div className="text-xs text-gray-400 truncate">{field.name}</div>
                      )}
                    </div>
                    {renderFieldSummaryChips(field)}
                    <div
                      className="flex items-center gap-2 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      {!isReadOnly && (
                        <>
                          <Tooltip content={field.enabled ? t("Désactiver") : t("Activer")}>
                            <div>
                              <Switch
                                isSelected={field.enabled}
                                onValueChange={(value) =>
                                  handleUpdateField(index, { enabled: value })
                                }
                                size="sm"
                              />
                            </div>
                          </Tooltip>
                          <button
                            type="button"
                            onClick={() => handleDeleteField(index)}
                            title={t("Supprimer")}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <BsTrash
                              size={14}
                              className="text-red-500"
                            />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-[#F0F0F0]">
                      <FieldForm
                        field={field}
                        onChange={(updates) => handleUpdateField(index, updates)}
                        isReadOnly={isReadOnly}
                        allFields={customFields}
                        departmentOptions={departmentOptions}
                        getDepartmentNames={getDepartmentNames}
                        t={t}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isDirty && !isReadOnly && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-3 bg-[#171717] text-white rounded-xl px-4 py-3 shadow-lg">
          <span className="text-sm">{t("Unsaved changes")}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              className="bg-white/10 text-white"
              onClick={handleCancel}
              isDisabled={isSaving}
            >
              {t("Annuler")}
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t("Sauvegarder les modifications")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsPanel;
